import axios from "axios";
import { logError } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.detail === "string") return detail.detail;
  return String(detail);
}

const MAX_RETRIES = 2;
const RETRY_BASE_MS = 300;
const RETRYABLE_STATUS = [502, 503, 504];

function isTransient(error) {
  if (error.code === "ECONNABORTED") return true; // timeout
  if (!error.response) return true;               // network / no response
  return RETRYABLE_STATUS.includes(error.response.status);
}

function friendlyMessage(error) {
  const status = error.response?.status;
  if (!error.response) return "Network error — please check your connection and try again.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 429) return "Too many requests. Please slow down and try again.";
  if (status >= 500) return "Something went wrong on our end. Please try again shortly.";
  return formatApiError(error.response?.data?.detail) || "Request failed. Please try again.";
}

// Centralized error handling: redacted logging + safe transient retry (GET/HEAD only).
// Request bodies are never logged (they may contain passwords/PII); the logger also
// redacts tokens, emails, and long numbers from the URL and server message.
async function retryIfTransient(error) {
  const config = error.config || {};
  const method = (config.method || "get").toLowerCase();
  const canRetry = ["get", "head"].includes(method) && isTransient(error);
  config.__retryCount = config.__retryCount || 0;
  if (!canRetry || config.__retryCount >= MAX_RETRIES) return null;
  config.__retryCount += 1;
  const delay = RETRY_BASE_MS * 3 ** (config.__retryCount - 1);
  await new Promise((resolve) => setTimeout(resolve, delay));
  return api(config);
}

function logRequestFailure(error) {
  const config = error.config || {};
  const method = (config.method || "get").toLowerCase();
  const status = error.response?.status;
  // The routine unauthenticated /auth/me probe on load is expected, not an error.
  const isAuthProbe = status === 401 && (config.url || "").includes("/auth/me");
  if (isAuthProbe) return;
  logError("api", "Request failed", {
    method: method.toUpperCase(),
    url: config.url,
    status: status ?? "network",
    serverMessage: error.response ? formatApiError(error.response?.data?.detail) : error.message,
    retries: config.__retryCount,
  });
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const retried = await retryIfTransient(error);
    if (retried) return retried;
    error.friendlyMessage = friendlyMessage(error);
    logRequestFailure(error);
    return Promise.reject(error);
  }
);

export default api;
